"use client"

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import SoundCard from "@/components/ui/sound-card"
import { StaticSoundRow } from "@/components/ui/sound-card/static-row"
import { ErrorBoundary } from "@/components/error-boundary"
import type { Sound } from "@/lib/supabase"

type SortKey = "popular" | "newest" | "alpha" | "shortest"

// Windowed hydration (mirrors app/sounds/sound-grid.tsx, TES-1077): only the
// first EAGER_CARD_RENDER cards mount on first paint; every slot past it
// renders the static row until it scrolls near the viewport. Collection
// pages can carry 200 sounds, and mounting every SoundCard (each runs the full
// audio/favorites/tracking hook stack) on one post-load task was the dominant
// mobile TBT cost that regressed this page-type (TES-834).
//
// The deferred slot is NOT an empty band. Collections are sitemapped hubs, and
// an empty placeholder cost the server HTML every sound link past the sixth
// (164 → 6 on gaming-nostalgia). StaticSoundRow carries the link, title and
// meta of every row from the first byte; only the hook stack is deferred.
const EAGER_CARD_RENDER = 6
const HYDRATION_ROOT_MARGIN = "400px 0px"

interface SortGridProps {
  sounds: Sound[]
  collectionLabel: string
  accentToken: string
  /** True when the fetch hit the render cap, so `sounds.length` is a floor. */
  isTruncated?: boolean
}

function CardSlot({
  sound,
  eager,
  accentToken,
}: {
  sound: Sound
  eager: boolean
  accentToken: string
}) {
  // Keep the server and hydration pass identical. The deferred browser fallback
  // below schedules an upgrade where IntersectionObserver is unavailable.
  const [hydrated, setHydrated] = useState(eager)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (hydrated) return
    if (typeof IntersectionObserver === "undefined") {
      const fallback = window.setTimeout(() => setHydrated(true), 0)
      return () => window.clearTimeout(fallback)
    }
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHydrated(true)
          observer.disconnect()
        }
      },
      { rootMargin: HYDRATION_ROOT_MARGIN }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hydrated])

  return (
    <div ref={ref}>
      {hydrated ? (
        <ErrorBoundary>
          <SoundCard
            sound={sound}
            showCategory={true}
            accentToken={accentToken}
            disableEntryAnimation
            deferAudioUntilVisible
          />
        </ErrorBoundary>
      ) : (
        <StaticSoundRow sound={sound} showCategory={true} />
      )}
    </div>
  )
}

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "popular", label: "Popular" },
  { key: "newest", label: "Newest" },
  { key: "alpha", label: "A–Z" },
  { key: "shortest", label: "Shortest" },
]

function compareBy(sounds: Sound[], sortKey: SortKey): Sound[] {
  const arr = [...sounds]
  switch (sortKey) {
    case "popular":
      return arr.sort((a, b) => (b.downloads ?? 0) - (a.downloads ?? 0))
    case "newest":
      return arr.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0
        return tb - ta
      })
    case "alpha":
      return arr.sort((a, b) => a.title.localeCompare(b.title))
    case "shortest":
      return arr.sort((a, b) => (a.duration ?? 0) - (b.duration ?? 0))
  }
}

export default function CollectionSortGrid({
  sounds,
  collectionLabel,
  accentToken,
  isTruncated = false,
}: SortGridProps) {
  const [sortKey, setSortKey] = useState<SortKey>("popular")
  const sorted = useMemo(() => compareBy(sounds, sortKey), [sounds, sortKey])

  return (
    <>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-lg font-semibold tracking-tight text-[var(--tls-fg-strong)]">
          <span className="tabular-nums">
            {sounds.length.toLocaleString("en-US")}
            {isTruncated ? "+" : ""}
          </span>{" "}
          {collectionLabel} sounds
        </h2>
        <div
          // eslint-disable-next-line react-doctor/prefer-tag-over-role -- role=group on a toolbar; no semantic HTML equivalent
          role="group"
          aria-label="Sort sounds"
          className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-1 rounded-[var(--radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] p-1 sm:flex-nowrap"
        >
          {SORT_OPTIONS.map((opt) => {
            const isActive = opt.key === sortKey
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                aria-pressed={isActive}
                // §02.4: painted 32 inside the segmented track, hit area
                // padded to 44 through the ::before box — targets are set by
                // pointer, and the paint is what the track geometry needs.
                className={`relative inline-flex h-8 items-center rounded-[var(--radius-pill)] px-3 text-xs font-semibold transition-[background-color,color] duration-[var(--motion-duration-fast)] before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] ${
                  isActive ? "tls-accent-chip" : ""
                }`}
                style={
                  isActive
                    ? // .tls-accent-chip paints the tint and derives an AA-safe
                      // ink from the accent (TES-1580) — do not re-add
                      // `color: rgb(var(accent))` here, it fails at 4.22:1.
                      ({ "--tls-accent-rgb": `var(${accentToken})` } as CSSProperties)
                    : {
                        background: "transparent",
                        color: "var(--tls-fg-muted)",
                      }
                }
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-0">
        {sorted.map((sound, index) => (
          <CardSlot
            key={sound.id}
            sound={sound}
            eager={index < EAGER_CARD_RENDER}
            accentToken={accentToken}
          />
        ))}
      </div>
    </>
  )
}
